import * as React from "react";
import {
  Box,
  Stack,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

export default function MLSTAllelesTableMUI({ alleles = {}, title = "mlst_alleles" }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (_e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const rows = React.useMemo(() => {
    return Object.entries(alleles || {}).map(([gene, allele]) => ({
      id: gene,
      gene,
      allele,
    }));
  }, [alleles]);

  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: "100%" }}>
      <ToolbarCSV
        data={{
          title,
          columns: [
            { key: "gene", label: "Gene" },
            { key: "allele", label: "Allele" },
          ],
          rows,
        }}
      />

      {/* 👇 SIN height fijo */}
      <Paper
        elevation={2}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table
            stickyHeader
            aria-label="mlst-alleles"
            sx={{
              width: "100%",
              tableLayout: "fixed",
              "& th": {
                bgcolor: "#000",
                color: "#fff",
                fontWeight: 700,
              },
              "& td, & th": { borderColor: "divider" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: "50%" }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Gene
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: "50%" }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Allele
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={700}>
                      {row.gene ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{row.allele ?? "—"}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación sigue funcionando */}
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </Box>
  );
}

/* ================= ToolbarCSV (igual a tu patrón) ================= */

// Escape RFC4180-ish: comillas dobles, comas, saltos de linea
const escapeCSV = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  const mustQuote = /[",\n\r]/.test(str);
  const escaped = str.replace(/"/g, '""');
  return mustQuote ? `"${escaped}"` : escaped;
};

const normalizeCell = (v) => (Array.isArray(v) ? v.join(", ") : v);

const objectToCSV = ({ columns = [], rows = [] }) => {
  const headers = columns.map((c) => escapeCSV(c.label ?? c.key ?? ""));
  const lines = [headers.join(",")];

  for (const r of rows) {
    const row = columns.map((c) => escapeCSV(normalizeCell(r?.[c.key])));
    lines.push(row.join(","));
  }
  return lines.join("\r\n");
};

const safeFilename = (name) =>
  String(name || "mlst_alleles")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

function ToolbarCSV({ data }) {
  const csv = React.useMemo(() => objectToCSV(data), [data]);

  const filename = React.useMemo(() => {
    const base = safeFilename(data?.title) || "mlst_alleles";
    return `${base}.csv`;
  }, [data?.title]);

  const copyCSV = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(csv);
        return;
      }
    } catch {
      // fallback
    }
    const ta = document.createElement("textarea");
    ta.value = csv;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  };

  const downloadCSV = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
      <Tooltip title="Copiar CSV">
        <IconButton size="small" onClick={copyCSV}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Descargar CSV">
        <IconButton size="small" onClick={downloadCSV}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}